import React, { useEffect, useState } from "react";
import { ConfigProvider, Popover, Tooltip } from "antd";
import { hasWebGL2 } from "./GeoBackdrop";
import LayerTrigger, { layerSummary } from "./LayerTrigger";
import LayerPanel from "./LayerPanel";
import {
  ACCENT,
  NEUTRAL_THEME,
  SURFACE,
  onActivateKey,
  stopPropagation,
} from "./layerStyle";
import "./layerControls.css";

/**
 * The 圖層 control on the canvas rail, shared by the live dashboard, replay,
 * the dashboard widget and the plan editor: one trigger that opens a panel
 * with the base layers (平面圖, 地圖) and the host's overlays (基站 / 物件 /
 * 機具 / 越線, and 可行區域 in the editor).
 *
 * Everything comes in through props: `toggles` from the host's own state,
 * `layers` from useMapLayers below.
 */

// ---------------------------------------------------------------- opacity store
// Per viewer, per site, in this browser only (never the database). Values are
// percentages in steps of 10. `mapSeen` records that the map has been turned
// on here once, which is when 平面圖 drops to 70 % (FIRST_MAP_PLAN_PCT).
const STORE_PREFIX = "iot3.geoLayers.";
const DEFAULT_OPACITY = Object.freeze({ plan: 100, map: 100, mapSeen: false });
const FIRST_MAP_PLAN_PCT = 70;

const pct = (value, fallback) =>
  Number.isFinite(value)
    ? Math.min(Math.max(Math.round(value), 0), 100)
    : fallback;

export const loadLayerOpacity = (siteId) => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(STORE_PREFIX + siteId),
    );
    if (!stored || typeof stored !== "object") return DEFAULT_OPACITY;
    return {
      plan: pct(stored.plan, DEFAULT_OPACITY.plan),
      map: pct(stored.map, DEFAULT_OPACITY.map),
      mapSeen: stored.mapSeen === true,
    };
  } catch (error) {
    return DEFAULT_OPACITY;
  }
};

export const saveLayerOpacity = (siteId, value) => {
  try {
    window.localStorage.setItem(STORE_PREFIX + siteId, JSON.stringify(value));
  } catch (error) {
    // Private mode or blocked storage: the values just last for this visit.
  }
};

// ---------------------------------------------------------------- state hook
/**
 * Map/plan layer state for one site. The map is available only on a plane
 * site with geo-location on, an origin, at least one tile layer and WebGL2
 * (probed only once all the rest holds). 地圖 starts OFF on every mount.
 * Without a map, 平面圖 is simply on (full opacity) or off. Only a plane site
 * has a 平面圖 layer (`hasPlan`); line and tunnel draw their own strip.
 */
export const useMapLayers = ({
  siteId,
  layoutType,
  geoEnabled,
  geoOriginLat,
  geoOriginLng,
  geoRotationDeg,
  geoTileLayers,
}) => {
  const tileLayers = geoTileLayers || [];
  const [mapOn, setMapOn] = useState(false);
  const [planOn, setPlanOn] = useState(true);
  const [gridOn, setGridOn] = useState(true);
  const [failed, setFailed] = useState(false);
  const [layerId, setLayerId] = useState(null);
  const [opacity, setOpacity] = useState(() => loadLayerOpacity(siteId));

  // The public dashboard changes site without remounting.
  useEffect(() => {
    setOpacity(loadLayerOpacity(siteId));
    setMapOn(false);
    setPlanOn(true);
    setGridOn(true);
    setFailed(false);
    setLayerId(null);
  }, [siteId]);

  const available =
    !!geoEnabled &&
    layoutType === "plane" &&
    geoOriginLat != null &&
    geoOriginLng != null &&
    tileLayers.length > 0 &&
    !failed &&
    hasWebGL2();

  const updateOpacity = (patch) => {
    const next = { ...opacity, ...patch };
    setOpacity(next);
    saveLayerOpacity(siteId, next);
  };

  const turnMapOn = () => {
    if (!opacity.mapSeen) {
      updateOpacity({ mapSeen: true, plan: FIRST_MAP_PLAN_PCT });
    }
    setMapOn(true);
  };

  const planFull = available ? opacity.plan / 100 : 1;
  return {
    available,
    hasPlan: layoutType === "plane",
    mapVisible: available && mapOn,
    planVisible: planOn,
    gridVisible: gridOn,
    mapOpacity: opacity.map / 100,
    planOpacity: planOn ? planFull : 0,
    mapPct: opacity.map,
    planPct: opacity.plan,
    layerId: tileLayers.some((l) => l.id === layerId)
      ? layerId
      : tileLayers[0]?.id,
    tileLayers,
    geoRef: {
      lat: geoOriginLat,
      lng: geoOriginLng,
      rotationDeg: geoRotationDeg ?? 0,
    },
    toggleMap: () => (mapOn ? setMapOn(false) : turnMapOn()),
    togglePlan: () => setPlanOn(!planOn),
    toggleGrid: () => setGridOn(!gridOn),
    showPlan: () => setPlanOn(true),
    // 恢復預設: plan and grid on, map off, the stored values back to a fresh site.
    reset: () => {
      setPlanOn(true);
      setGridOn(true);
      setMapOn(false);
      setOpacity(DEFAULT_OPACITY);
      saveLayerOpacity(siteId, DEFAULT_OPACITY);
    },
    setMapPct: (value) => updateOpacity({ map: pct(value, opacity.map) }),
    setPlanPct: (value) => updateOpacity({ plan: pct(value, opacity.plan) }),
    setLayerId,
    markUnavailable: () => setFailed(true),
  };
};

// ---------------------------------------------------------------- control
const isAtDefault = (toggles, layers) =>
  toggles.every((t) => t.visible) &&
  (!layers ||
    (layers.planVisible &&
      layers.gridVisible &&
      !layers.mapVisible &&
      layers.planPct === DEFAULT_OPACITY.plan &&
      layers.mapPct === DEFAULT_OPACITY.map));

/**
 * @param {{label:string,count:number,visible:boolean,onToggle:Function,hint?:React.ReactNode}[]} toggles
 *   the host's overlays, in order; a row with count 0 is disabled, never dropped
 * @param {object} [layers]  useMapLayers() result
 */
const LayerControls = ({ toggles, layers }) => {
  const [open, setOpen] = useState(false);
  if (!layers?.hasPlan && toggles.length === 0) return null;

  const hiddenLabels = toggles
    .filter((t) => t.count > 0 && !t.visible)
    .map((t) => t.label);
  const summary = layerSummary({
    mapOn: !!layers?.mapVisible,
    planOff: !!layers?.hasPlan && !layers.planVisible,
    hiddenLabels,
  });
  const reset = () => {
    toggles.filter((t) => !t.visible).forEach((t) => t.onToggle());
    layers?.reset?.();
  };
  // Escape closes the panel (and stops there, so the plan editor's modal
  // stays open), then puts focus back on the trigger.
  const closeOnEscape = (event) => {
    if (event.key !== "Escape" || !open) return;
    event.stopPropagation();
    setOpen(false);
    event.currentTarget.querySelector(".iot3-layer-trigger")?.focus();
  };

  return (
    // The wrapper keeps every press (the trigger, and the panel's events,
    // which bubble here through the portal) from panning the canvas or
    // reaching the plan editor's click handler.
    <span
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onKeyDown={closeOnEscape}
      style={{ display: "inline-flex", pointerEvents: "auto" }}
    >
      <ConfigProvider theme={NEUTRAL_THEME}>
        <Popover
          trigger="click"
          placement="bottomRight"
          arrow={false}
          open={open}
          onOpenChange={setOpen}
          classNames={{ root: "iot3-layer-panel" }}
          styles={{
            root: { "--iot3-layer-accent": ACCENT },
            body: { padding: 0, backgroundColor: SURFACE },
          }}
          content={
            <LayerPanel
              toggles={toggles}
              layers={layers}
              isDefault={isAtDefault(toggles, layers)}
              onReset={reset}
            />
          }
        >
          <Tooltip
            title={
              summary === "圖層"
                ? "切換底圖與顯示項目"
                : summary.replace("圖層：", "")
            }
            open={open ? false : undefined}
            placement="left"
          >
            <LayerTrigger
              open={open}
              mapOn={!!layers?.mapVisible}
              hiddenCount={hiddenLabels.length}
              summary={summary}
              onKeyDown={onActivateKey(() => setOpen(!open))}
            />
          </Tooltip>
        </Popover>
      </ConfigProvider>
    </span>
  );
};

export default LayerControls;
